import React, { useState, useEffect } from 'react';
import '../../../Styles/StickyStatusBar.css';
import { motion, AnimatePresence } from 'framer-motion';
import { useFeatureFlags } from '../../../FeatureFlags/FeatureFlagsContext';
import { FEATURE_FLAGS } from '../../../FeatureFlags/featureFlagConstants';

const StickyStatusBar = ({
  statusData = [],     // ✅ default to array (your old code used {})
  scheduleData = null,
  recurringData = [],         // ✅ NEW
  loading = false,     // optional: shows "Updating…" text
  error = null,        // optional: polling/network error text
  errorMsgs = [],      // optional: [{ tenantUrl, errorMsg }]
  onCancelSchedule,
  onEditSchedule,

  onCancelRecurring,          // ✅ NEW (optional)
  onEditRecurring,            // ✅ NEW (optional)
  onToggleRecurringDay,       // ✅ NEW (optional, future inline toggle)
  onDisableRecurring,
  onCancelCurrent
}) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const enableIngestionScheduling = isFeatureEnabled(FEATURE_FLAGS.EnableIngestionScheduling);
  const [expanded, setExpanded] = useState(false);
  const [openTenants, setOpenTenants] = useState({}); // Track dropdowns per tenant
  const [activeTab, setActiveTab] = useState('current'); // 'current' | 'scheduled'

  useEffect(() => {
    if (!enableIngestionScheduling && activeTab !== 'current') {
      setActiveTab('current');
    }
  }, [enableIngestionScheduling, activeTab]);
  // props (add one optional callback)
  // (row) => void   // optional

  // state: which row's kebab is open
  const [openRecMenu, setOpenRecMenu] = useState(null); // row.id | null


  const DEFAULT_PANEL_H = 400;
  const MIN_PANEL_H = 220;
  const LS_KEY = 'yads_sticky_panel_h';

  const getMaxPanelH = () => Math.floor(window.innerHeight * 0.8); // 80vh

  const [panelH, setPanelH] = useState(() => {
    const saved = Number(localStorage.getItem(LS_KEY));
    return saved && !Number.isNaN(saved) ? saved : DEFAULT_PANEL_H;
  });

  const resizeRef = React.useRef({
    dragging: false,
    startY: 0,
    startH: DEFAULT_PANEL_H,
  });

  useEffect(() => {
    const onResize = () => {
      setPanelH((h) => Math.min(Math.max(h, MIN_PANEL_H), getMaxPanelH()));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const clampH = (h) => Math.min(Math.max(h, MIN_PANEL_H), getMaxPanelH());

  const onResizeDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    resizeRef.current.dragging = true;
    resizeRef.current.startY = e.clientY;
    resizeRef.current.startH = panelH;

    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!resizeRef.current.dragging) return;

      // dragging UP should increase height
      const delta = resizeRef.current.startY - e.clientY;
      const next = clampH(resizeRef.current.startH + delta);
      setPanelH(next);
    };

    const onUp = () => {
      if (!resizeRef.current.dragging) return;

      resizeRef.current.dragging = false;
      document.body.style.userSelect = '';
      localStorage.setItem(LS_KEY, String(panelH));
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [panelH]);


  // close on outside click / tab switch / collapse
  useEffect(() => { if (!expanded) setOpenRecMenu(null); }, [expanded, activeTab]);
  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest?.('.rec-actions-cell')) setOpenRecMenu(null);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);


  const formatScenarioName = (sc) => {
  const title = sc?.name || sc?.title || "—";

  // only change for duration mode
  const mode = String(sc?.runMode ?? "").toLowerCase().trim();
  if (mode !== "duration") return title;

  const hrs = parseInt(sc?.durationHours, 10);
  if (!hrs || hrs <= 0) return title;

  // keep same pattern, just append hint
  return `${title} (${hrs}h)`;
};

  // handlers
  const toggleRecMenu = (rowId) =>
    setOpenRecMenu((curr) => (curr === rowId ? null : rowId));

  const handleRecEdit = (row) => { setOpenRecMenu(null); onEditRecurring?.(row); };
  const handleRecDisable = (row) => { setOpenRecMenu(null); onDisableRecurring?.(row); };
  const handleRecDelete = (row) => { setOpenRecMenu(null); onCancelRecurring?.(row); };



  const formatHms = (secs) => {
    const s = Math.max(0, Math.floor(Number(secs) || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    // 0h 05m 09s
    return `${h}h ${String(m).padStart(2, '0')}m ${String(sec).padStart(2, '0')}s`;
  };

  const renderDuration = (item) => {
    const d = item?.duration;

    // If backend already sends a pretty string like "0h 59m 17s", show it.
    if (typeof d === 'string' && d.trim()) return d;

    // If backend sends seconds as a number, format it.
    if (typeof d === 'number') return formatHms(d);

    // No duration yet (pending / in-progress without elapsed provided)
    if (item?.status?.inProgress) return '—';

    // Done/Failed but backend didn’t include duration
    if (item?.status?.done || item?.status?.failed) return '—';

    // Fallback
    return '—';
  };

  const renderCompletedAt = (item) => {
    // Only makes sense for finished rows; show dash for running/queued
    if (!(item?.status?.done || item?.status?.failed)) return '—';
    return item.completedAt || '—';
  };




  const toggleExpand = () => setExpanded(!expanded);
  const toggleTenant = (tenantKey) => {
    setOpenTenants((prev) => ({ ...prev, [tenantKey]: !prev[tenantKey] }));
  };

  const ingestionData = Array.isArray(statusData) ? statusData : [];
  const anyFailed = ingestionData.some(
    t => t?.status?.failed || (t?.errorText && t.errorText.trim() !== '')
  );

  // count only tenants with in-progress
  const totalRunning = ingestionData.filter(t => t?.status?.inProgress).length;
  const totalQueued = ingestionData.filter(t => t?.status?.queued).length;


  // count only tenants with failed
  const totalFailed = ingestionData.filter(
    t =>
      t?.status?.failed ||
      (t?.errorText && t.errorText.trim() !== '' && !t?.status?.cancelled)
  ).length;


  // ===== Scheduled (dummy fallback) ===
  const scheduleRows = Array.isArray(scheduleData) ? scheduleData : [];
  const totalScheduled = scheduleRows.length;


  const recurringRows = Array.isArray(recurringData) ? recurringData : [];
  const totalRecurring = recurringRows.length;


  // helpers
  const plural = (n, s, p = s + 's') => `${n} ${n === 1 ? s : p}`;

  // Format days list (e.g., ["MO","WE","FR"] -> "Mon, Wed, Fri")
  const DAY_LABELS = { MO: 'Mon', TU: 'Tue', WE: 'Wed', TH: 'Thu', FR: 'Fri', SA: 'Sat', SU: 'Sun' };
  const formatDays = (codes = []) => codes.map(c => DAY_LABELS[c] || c).join(', ');


  const hasCurrent = ingestionData.length > 0;


  return (
    <div className={`sticky-status-bar ${expanded ? 'expanded' : ''}`}>


      {/* ✅ Resize handle ABOVE header */}
      {expanded && (
        <div
          className="status-resize-handle status-resize-handle--above"
          onMouseDown={(e) => {
            e.stopPropagation();          // ✅ don’t trigger header toggle
            onResizeDown(e);              // your existing resize handler
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            setPanelH(DEFAULT_PANEL_H);
            localStorage.setItem(LS_KEY, String(DEFAULT_PANEL_H));
          }}
          title="Drag to resize (double click to reset)"
        >
          <span className="status-resize-grip" />
        </div>
      )}


      {/* 🔁 EXACT same expand behavior: click header toggles */}
      <div className="status-header" onClick={toggleExpand}>
        <span className="status-header-left">
          {loading && <span className="mini-spinner" aria-label="Updating" />}
          {anyFailed && <span className="status-error-dot" title="Failures present" />}
          <span>
            🔄 {loading ? 'Updating…' : `${totalRunning} Ingestion${totalRunning !== 1 ? 's' : ''} running`}
            {totalQueued > 0 && <span style={{ marginLeft: 8 }}>⏳ {totalQueued} Pending</span>}
            {totalFailed > 0 && <span style={{ color: 'red', marginLeft: 8 }}>⚠️ {totalFailed} Failed</span>}
            {enableIngestionScheduling && (
              <>
                <span style={{ marginLeft: 12 }}>📅 {plural(totalScheduled, 'Schedule')}</span>
                <span style={{ marginLeft: 12 }}>♻️ {plural(totalRecurring, 'Recurring')}</span>
              </>
            )}
          </span>

        </span>
        <button>{expanded ? 'Hide Status ▲' : 'Show Status ▼'}</button>
      </div>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            className="status-content-shell"
            key="status-content"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >

            {/* Scroll area with user-defined height */}
            <div className="status-content" style={{ maxHeight: panelH, overflowY: 'auto' }}>

              {/* Tabs */}
              <div className="yads-tabs">
                <button
                  type="button"
                  className={`yads-tab ${activeTab === 'current' ? 'active' : ''}`}
                  onClick={() => setActiveTab('current')}
                >
                  Running {totalRunning > 0 ? `(${totalRunning})` : ''}
                </button>
                {enableIngestionScheduling && (
                  <>
                    <button
                      type="button"
                      className={`yads-tab ${activeTab === 'scheduled' ? 'active' : ''}`}
                      onClick={() => setActiveTab('scheduled')}
                    >
                      One-time {totalScheduled > 0 ? `(${totalScheduled})` : ''}
                    </button>
                    <button
                      type="button"
                      className={`yads-tab ${activeTab === 'recurring' ? 'active' : ''}`}
                      onClick={() => setActiveTab('recurring')}
                    >
                      Recurring {totalRecurring > 0 ? `(${totalRecurring})` : ''}
                    </button>
                  </>
                )}
              </div>


              {/* ====== CURRENT TAB (NEW) ====== */}
              {activeTab === 'current' && (
                <div className="yads-current">
                  <div className="yads-current__scroll">
                    <div className="yads-current__grid yads-current__grid--head">
                      <div>Tenant</div>
                      <div>Scenarios</div>
                      <div>Status</div>
                      <div className="is-right">Duration</div>
                      <div className="is-right">Completed At</div>
                      <div className="is-center">Action</div>
                    </div>

                    {ingestionData.map((item, index) => (
                      <div key={index} className="yads-current__grid yads-current__grid--row">
                        <div className="yads-tenant" title={item.tenant}>
                          {item.tenant}
                        </div>

                        <div className="yads-scenarios">

                          {item.scenarios.map((sc, i) => {
                            const isCompleted = sc.type === 'success';

                            return (
                              <div key={i} className="yads-scn-wrap">
                                <div className={`yads-scn yads-scn--${sc.type}`}>
                                  <span className={`yads-scn__text ${sc.type === 'cancelled' ? 'is-cancelled' : ''}`}>
                                    {formatScenarioName(sc)}
                                  </span>

                                  {isCompleted && <span className="yads-scn__icon">✅</span>}
                                </div>
                              </div>
                            );
                          })}


                          {item.errorText && !item.status.cancelled && (
                            <div className="yads-error">{item.errorText}</div>
                          )}
                        </div>

                        <div className="yads-status">
                          {item.status.done && <span className="yads-badge yads-badge--done">Done</span>}

                          {item.status.inProgress && (
                            <span className="yads-badge yads-badge--progress">
                              <span className="yads-spin" />
                              In Progress
                            </span>
                          )}

                          {item.status.queued && (
                            <span className="yads-badge yads-badge--queued">
                              Pending
                              <span className="yads-dots"><i /><i /><i /></span>
                            </span>
                          )}

                          {item.status.cancelRequested && (
                            <span className="yads-badge yads-badge--cancel-req yads-badge--wrap">
                              Cancellation Requested
                            </span>
                          )}

                          {item.status.cancelled && (
                            <span className="yads-badge yads-badge--cancelled">
                              Cancelled
                            </span>
                          )}


                          {item.status.failed && <span className="yads-badge yads-badge--fail">Failed</span>}
                        </div>

                        <div className="yads-duration">{renderDuration(item)}</div>

                        <div className="yads-completed">{renderCompletedAt(item)}</div>

                        <div className="yads-action">
                          {(item.status.queued || item.status.inProgress) &&
                            !item.status.cancelRequested &&
                            !item.status.cancelled &&
                            onCancelCurrent && (
                              <button
                                type="button"
                                className="yads-cancel"
                                onClick={() => onCancelCurrent(item)}
                              >
                                Cancel
                              </button>
                            )}
                        </div>
                      </div>
                    ))}

                    <center>{!hasCurrent && <div className="yads-empty">No ingestions currently running or pending</div>}</center>
                  </div>
                </div>
              )}



              {/* ====== SCHEDULED TAB ====== */}
              {enableIngestionScheduling && activeTab === 'scheduled' && (
                <>
                  <div className="schedule-columns-header">
                    <div className="tenant">Tenant</div>
                    <div className="scenarios">Scenarios</div>
                    <div className="scheduledFor">Scheduled For</div>
                    <div className="action">Action</div>
                  </div>

                  {scheduleRows.map((row, idx) => (
                    <motion.div key={row.id || idx} className="schedule-row">
                      <div className="tenant">{row.tenant}</div>
                      <div className="scenarios">
                        {row.scenarios?.map((s, i) => (
                          <span key={i} className="scenario-chip">{s}</span>
                        ))}
                      </div>
                      <div
                        className="scheduledFor"
                        title={`UTC: ${row._raw?.scheduledDate || ''}`}
                      >
                        {row.scheduledFor}
                      </div>

                      <div className="action">
                        <button type="button" className="link-btn" onClick={() => onEditSchedule?.(row)}>Edit</button>
                        <button type="button" className="link-btn danger" onClick={() => onCancelSchedule?.(row)}>Cancel</button>
                      </div>
                    </motion.div>
                  ))}

                  {scheduleRows.length === 0 && (
                    <div className="empty-schedule">No scheduled ingestions.</div>
                  )}
                </>
              )}



              {/* ===== RECURRING TAB ===== */}
              {enableIngestionScheduling && activeTab === 'recurring' && (
                <>
                  <div className="rec-columns-header rec--6">
                    <div className="tenant">Tenant</div>
                    <div className="scenarios">Scenarios</div>
                    <div className="frequency">Frequency</div>
                    <div className="days">Active Days</div>
                    <div className="time">Time</div>
                    <div className="action">Action</div>
                  </div>

                  {recurringRows.map((row, idx) => {
                    const rowKey = row.id ?? idx;
                    const disabled = row.enabled === false;
                    return (
                      <motion.div key={rowKey} className={`rec-row rec--6 ${disabled ? 'rec-row--disabled' : ''}`}>
                        <div className="tenant">{row.tenant}</div>

                        <div className="scenarios">
                          {row.scenarios?.map((s, i) => (
                            <span key={i} className="scenario-chip">{s}</span>
                          ))}
                        </div>

                        <div className="frequency">
                          {row.frequency === 'Weekly' ? 'Weekly' : 'Weekly'}
                        </div>

                        <div className="days">
                          <div className="days-pills days-pills--compact">
                            {['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'].map(code => {
                              const active = (row.daysOfWeek || []).includes(code);
                              const label = (code === 'TU' ? 'T' : code[0]); // M T W T F S S
                              return (
                                <span
                                  key={code}
                                  className={`day-pill day-pill--compact ${active ? 'is-active' : ''}`}
                                  title={code}
                                >
                                  {label}
                                </span>
                              );
                            })}
                          </div>
                        </div>

                        <div className="time">{row.timeText || row.time || '--'}</div>

                        {/* ACTIONS (kebab) */}
                        <div className="rec-actions-cell">
                          <button
                            type="button"
                            className="kebab-btn"
                            aria-haspopup="menu"
                            aria-expanded={openRecMenu === rowKey}
                            onClick={(e) => { e.stopPropagation(); toggleRecMenu(rowKey); }}
                            title="More"
                          >
                            ⋮
                          </button>

                          <AnimatePresence>
                            {openRecMenu === rowKey && (
                              <motion.div
                                className="rec-menu"
                                role="menu"
                                initial={{ opacity: 0, scale: 0.98, y: -2 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.98, y: -2 }}
                                transition={{ duration: 0.12 }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button role="menuitem" className="rec-menu__item" onClick={() => handleRecEdit(row)}>Edit</button>
                                <button
                                  role="menuitem"
                                  className="rec-menu__item"
                                  onClick={() => handleRecDisable(row)}
                                >
                                  {row.enabled ? 'Disable' : 'Enable'}
                                </button>
                                <button role="menuitem" className="rec-menu__item danger" onClick={() => handleRecDelete(row)}>Delete</button>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </motion.div>
                    );
                  })}

                  {recurringRows.length === 0 && (
                    <div className="empty-schedule">No recurring schedules.</div>
                  )}
                </>
              )}


            </div>

          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StickyStatusBar;
